export interface CatalogWhatsAppOrderItem {
    name: string;
    variantName?: string | null;
    quantity: number;
    price: number;
    unitName?: string | null;
}

export interface CatalogWhatsAppCustomField {
    name: string;
    value: any;
}

export interface GenerateCatalogOrderWhatsAppMessageParams {
    catalogName?: string;
    businessName?: string;
    customerName?: string;
    customerPhone?: string;
    customerAddress?: string;
    customFields?: CatalogWhatsAppCustomField[];
    cart: CatalogWhatsAppOrderItem[];
    currency: string;
    cartTotal: number;
    paymentMode?: string;
    orderNote?: string;
}

/**
 * Constructs a structured WhatsApp order message for Simply Catalog orders.
 */
export const generateCatalogOrderWhatsAppMessage = ({
    catalogName,
    businessName,
    customerName,
    customerPhone,
    customerAddress,
    customFields = [],
    cart = [],
    currency = "$",
    cartTotal = 0,
    paymentMode,
    orderNote,
}: GenerateCatalogOrderWhatsAppMessageParams): string => {
    let message = ` *New Order: ${catalogName || businessName || "Catalog"}*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += ` *Customer Details:*\n`;

    if (customerName && customerName.trim()) {
        message += ` *Name:* ${customerName.trim()}\n`;
    }
    if (customerPhone && customerPhone.trim()) {
        message += ` *Phone:* ${customerPhone.trim()}\n`;
    }
    if (customerAddress && customerAddress.trim()) {
        message += ` *Address:* ${customerAddress.trim()}\n`;
    }

    if (customFields.length > 0) {
        customFields.forEach((cf) => {
            const displayVal = Array.isArray(cf.value) ? cf.value.join(", ") : cf.value;
            message += ` *${cf.name}:* ${displayVal}\n`;
        });
    }

    message += `\n *Order Items:*\n`;
    cart.forEach((item, idx) => {
        const varText = item.variantName ? ` (${item.variantName})` : "";
        const unitText = item.unitName ? ` ${item.unitName}` : "";
        const safePrice = Number(item.price) || 0;
        const safeQty = Number(item.quantity) || 0;
        const priceDisplay = Number.isInteger(safePrice) ? safePrice.toString() : safePrice.toFixed(2);
        const lineTotal = (safePrice * safeQty).toFixed(2);
        message += `${idx + 1}. *${item.name}${varText}*\n   Qty: ${safeQty}${unitText} × ${currency}${priceDisplay} = *${currency}${lineTotal}*\n`;
    });

    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += ` *Total Amount:* *${currency}${(Number(cartTotal) || 0).toFixed(2)}*\n`;

    if (paymentMode && paymentMode.trim()) {
        message += ` *Payment Mode:* ${paymentMode.trim()}\n`;
    }

    if (orderNote && orderNote.trim()) {
        message += ` *Note:* ${orderNote.trim()}\n`;
    }

    message += `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `Order generated via Simply Catalog.`;

    return message;
};

/**
 * Opens WhatsApp in a new tab with the prepared message.
 */
export const openWhatsAppOrder = (
    phoneNumber?: string | null,
    message: string = ""
): void => {
    if (typeof window === "undefined") return;

    const rawNumber = (phoneNumber || "").replace(/\D/g, "");
    const encodedMsg = encodeURIComponent(message);

    const waUrl = rawNumber
        ? `https://wa.me/${rawNumber}?text=${encodedMsg}`
        : `https://wa.me/?text=${encodedMsg}`;

    window.location.assign(waUrl);
};
